import React, { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, UserRound } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { Button } from "@/src/components/ui/button";

import LoginField from "./LoginField";
import { createLoginSchema } from "./loginSchemas";

import type { JSX } from "react";

import type { LoginFormValues } from "./loginSchemas";

interface LoginAdminDefaultFormProps {
  onSubmit: (values: LoginFormValues) => void;
  pathToResetPassword: string;
}

function LoginAdminDefaultForm({
  onSubmit,
  pathToResetPassword,
}: LoginAdminDefaultFormProps): JSX.Element {
  const { t } = useTranslation("login");

  const schema = useMemo(() => createLoginSchema(t), [t]);
  const methods = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <form
        noValidate
        className="flex w-full flex-col gap-5"
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <LoginField
          autoComplete="email"
          icon={UserRound}
          label={t("emailLabel")}
          name="email"
          placeholder={t("emailContent")}
          type="email"
        />
        <LoginField
          withVisibilityToggle
          autoComplete="current-password"
          icon={Lock}
          label={t("passwordLabel")}
          name="password"
          placeholder={t("passwordContent")}
          type="password"
        />

        <div className="mt-3 flex flex-col items-center gap-3">
          <Button className="w-full" size="lg" type="submit">
            {t("loginButtonLabel")}
          </Button>
          <NavLink
            className="text-muted-foreground hover:text-foreground text-xs"
            to={pathToResetPassword}
          >
            {t("forgotPassword")}
          </NavLink>
        </div>
      </form>
    </FormProvider>
  );
}

export default LoginAdminDefaultForm;
